<template>
  <v-layout
    align-center
    justify-center
  >
    <v-flex
      v-if="!isEmpty(schema)"
      xs12
      sm8
      md8
    >
      <h1 class="display-1 primary--text">
        {{ $t('message.targets') }}
      </h1>
      <user-info
        v-if="$store.state.user.isAdmin && farmer._id"
        class="elevation-1 mb-3"
        :user="farmer"
      />
      <div v-if="!isEmpty(targets)">
        <v-alert
          :value="formOptions.disableAll && !targets.approvedOn"
          type="warning"
        >
          {{ $t('message.targets_not_approved_yet') }}
        </v-alert>

        <v-alert
          :value="targets.approvedOn"
          type="success"
        >
          {{ $t('message.targets_approved') }}
        </v-alert>

        <v-alert
          :value="targets.adminNotes && !targets.approvedOn"
          type="error"
        >
          {{ $t('message.adminNotes') }}:
          <div class="pre-formatted">
            {{ targets.adminNotes }}
          </div>
        </v-alert>
      </div>

      <v-jsonschema-form
        v-if="showTargetsForm"
        ref="dynamicForm"
        :schema="schema"
        :model="targets"
        :options="formOptions"
        @error="showError"
        @change="change"
      />
      <v-alert
        v-else
        :value="true"
        type="warning"
      >
        {{ $t('message.cannot_add_targets_before_indicators_approval') }}
      </v-alert>

      <submit-draft-dialog
        v-if="showTargetsForm && !$store.state.user.isAdmin"
        v-model="dialog"
        :enable-submit="!formOptions.disableAll"
        @confirm="submitDraft"
      />

      <v-btn
        v-if="$store.state.user.isAdmin"
        color="primary"
        dark
        :to="{ name: 'targetslist' }"
      >
        <v-icon
          left
          dark
        >
          fa-arrow-left
        </v-icon>{{ $t('message.back_to_targets_button') }}
      </v-btn>

      <return-for-corrections-dialog
        v-if="$store.state.user.isAdmin"
        v-model="setToDraftDialog"
        :admin-notes="targets.adminNotes"
        :disabled="targets.isDraft"
        @confirm="setToDraft"
      />

      <approve-dialog
        v-if="$store.state.user.isAdmin"
        v-model="approveTargetsDialog"
        :disabled="targets.isDraft"
        @confirm="approveTargets"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import VJsonschemaForm from '@koumoul/vuetify-jsonschema-form'
import feathersClient from '../feathers-client'
import UserInfo from '@/components/admin/UserInfo'
import SubmitDraftDialog from '@/components/user/SubmitDraftDialog'
import ReturnForCorrectionsDialog from '@/components/admin/ReturnForCorrectionsDialog'
import ApproveDialog from '@/components/admin/ApproveDialog'
import TargetIndicatorCommon from '@/mixins/TartgetIndicatorCommon'
import isEmpty from 'lodash/isEmpty'

const formatDate = function (d) {
  return d ? d.split('T')[0] : ''
}

export default {
  name: 'Targets',
  components: {
    VJsonschemaForm,
    UserInfo,
    SubmitDraftDialog,
    ReturnForCorrectionsDialog,
    ApproveDialog,
  },
  mixins: [TargetIndicatorCommon],
  data() {
    return {
      targets: '',
      currentSituation: {},
      sch: '',
      formValid: false,
      dialog: false,
      approveTargetsDialog: false,
      setToDraftDialog: false,
      farmer: {},
    }
  },
  computed: {
    ...mapState({
      indicatorsList: (state) => state.indicatorsList,
      schema: (state) => state.targetsSchema,
      user: (state) => state.user,
    }),
    formOptions() {
      return {
        debug: false,
        disableAll:
          !this.$store.state.user.isAdmin &&
          this.targets._id != '' &&
          typeof this.targets._id === 'string' &&
          !this.targets.isDraft,
        autoFoldObjects: true,
        accordionMode: 'popout',
        targets: !isEmpty(this.currentSituation)
          ? this.flattenObject(this.currentSituation)
          : undefined,
      }
    },
    showTargetsForm() {
      if (isEmpty(this.targets)) return false
      let isIndicatorApproved =
        !isEmpty(this.indicatorsList) &&
        !isEmpty(_.first(this.indicatorsList).approvedOn)
      return this.user.isAdmin ? true : isIndicatorApproved
    },
  },
  created() {
    document.addEventListener('beforeunload', this.beforeUnload)
    this.$store.subscribe((mutation, state) => {
      switch (mutation.type) {
      case 'onTargetsUpdated':
        mutation.payload.date = formatDate(mutation.payload.date)
        if (this.$refs.dynamicForm) {
          Object.keys(mutation.payload).forEach((key) => {
            if (!['updatedAt', 'createdAt'].includes(key)) {
              Vue.set(
                this.$refs.dynamicForm.modelWrapper.root,
                key,
                mutation.payload[key]
              )
            }
          })
        }
        if (mutation.payload.isDraft === false) {
          //FIXME: the vue-i18n translation doesn't seem to always work here. See https://github.com/kazupon/vue-i18n/issues/184
          this.$store.commit(
            'showSnackbarMessage',
            'Concept ingediend ter goedkeuring.'
          )
        }
        break
      case 'onTargets':
        const targets = mutation.payload.data[0]
        if (targets === undefined) {
          this.targets = { date: '', _id: '' }
        } else {
          targets.date = formatDate(targets.date)
          this.targets = targets
        }
        this.getCurrentSituation(this.targets.userId)
          .then((res) => {
            this.currentSituation = res.data[0]
          })
          .catch((err) => console.log(err))
        if (this.user.isAdmin) this.getFarmer(this.targets.userId)
        break
      }
    })
  },
  mounted() {
    this.targets = ''

    this.$store.dispatch('getTargets', {
      query: {
        _id:
          this.$router.currentRoute.params.targetId == 'own'
            ? undefined
            : this.$router.currentRoute.params.targetId,
      },
    })
    if (!this.user.isAdmin) {
      this.$store.dispatch('getIndicatorsList')
    }
  },
  methods: {
    beforeUnload() {},
    getCurrentSituation(userId) {
      return feathersClient.service('indicators').find({
        query: {
          $limit: 1,
          $sort: {
            createdAt: 1,
          },
          userId: userId,
        },
      })
    },
    isEmpty: isEmpty,
    showError(err) {
      window.alert(err)
    },
    change(e) {
      //save the data to the server on change
      const obj = e.key
        .split('.')
        .reverse()
        .reduce((o, k) => ({ [k]: o }), e.model)
      obj._id = this.targets._id
      obj.userId = this.targets.userId
      this.$store.dispatch('saveIndicatorsOrTargets', {
        data: obj,
        serviceName: 'targets',
      })
    },
    submitDraft() {
      //save the data to the server on change
      this.targets.isDraft = false
      this.$store.dispatch('saveIndicatorsOrTargets', {
        data: this.targets,
        serviceName: 'targets',
      })
      this.dialog = false
    },
    approveTargets() {
      this.approveTargetsDialog = false
      this.$store.dispatch('saveIndicatorsOrTargets', {
        data: { _id: this.targets._id, approvedOn: new Date() },
        serviceName: 'targets',
      })
    },
    setToDraft(notes) {
      this.setToDraftDialog = false
      this.$store.dispatch('saveIndicatorsOrTargets', {
        data: {
          _id: this.targets._id,
          isDraft: true,
          approvedOn: null,
          adminNotes: notes,
        },
        serviceName: 'targets',
      })
    },
    getFarmer(userId) {
      feathersClient
        .service('users')
        .get(userId)
        .then((res) => {
          this.farmer = res
        })
        .catch((err) => console.log(err))
    },
  },
}
</script>
<style>
.v-subheader.theme--light {
  background-color: #eee;
  box-shadow: 1px 1px 3px #ddd;
  margin-bottom: 0.5em;
}
.pre-formatted {
  white-space: pre;
}
</style>
